import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet';

const faqs = [
  {
    question: 'What areas do you cover for same-day delivery?',
    answer: 'We cover all of Berkshire and Hampshire including Reading, Bracknell, Slough, Newbury, Wokingham, Windsor, Maidenhead and surrounding areas. We can collect and deliver within a 50-mile radius of Bracknell.'
  },
  {
    question: 'How quickly can you collect my parcel?',
    answer: 'For same-day delivery (1-12 hours), we typically collect within 1-2 hours of booking. For urgent delivery (0-6 hours), we can collect within 30-60 minutes depending on your location in Reading, Bracknell, or Slough.'
  },
  {
    question: 'What sizes of vehicles do you have?',
    answer: 'We have a fleet of 5 vehicle types: Small Van (up to 3.5T), SWB (Short Wheelbase), MWB (Medium Wheelbase), XLWB (Extra Long Wheelbase), and Luton Van. Each vehicle can accommodate different load sizes from small parcels to pallets.'
  },
  {
    question: 'Do you provide goods in transit insurance?',
    answer: 'Yes, all deliveries are covered by £50,000 goods in transit insurance as standard. This protects your items throughout the journey from collection to delivery.'
  },
  {
    question: 'How much does same-day delivery cost in Reading?',
    answer: 'Same-day delivery in Reading starts from £20 for small van deliveries within 0-60 miles. Prices vary based on distance, vehicle type, and urgency. Use our instant quote calculator or call 01344 508095 for exact pricing.'
  },
  {
    question: 'Can I track my delivery in real-time?',
    answer: 'Yes! Once your delivery is confirmed, you receive a unique tracking ID. You can track your parcel status, driver information, and estimated delivery time on our tracking page.'
  },
  {
    question: 'What is the difference between same-day and urgent delivery?',
    answer: 'Same-day delivery (1-12 hours) is our standard service with collection within 1-2 hours and delivery the same day. Urgent delivery (0-6 hours) is express service with immediate collection within 30-60 minutes and faster delivery times.'
  },
  {
    question: 'Do you deliver on weekends and bank holidays?',
    answer: 'Yes, we operate 24/7 including weekends and bank holidays. Our customer service team is available round the clock at 01344 508095 to book your delivery.'
  },
  {
    question: 'What items can you deliver?',
    answer: 'We deliver a wide range of items including documents, parcels, pallets, furniture, electronics, and business supplies. We cannot deliver hazardous materials, illegal items, or perishable goods without proper packaging.'
  },
  {
    question: 'How do I get a quote for delivery from Bracknell to Slough?',
    answer: 'You can get an instant quote by using our online booking form on the website, calling us at 01344 508095, or using the price calculator in our admin portal. Prices depend on distance, vehicle size, and delivery speed.'
  },
  {
    question: 'What is your on-time delivery rate?',
    answer: 'We maintain a 98% on-time delivery rate across all our services in Berkshire and Hampshire. We prioritize punctuality and reliability for all business and personal deliveries.'
  },
  {
    question: 'Do you offer regular delivery contracts for businesses?',
    answer: 'Yes, we offer tailored B2B delivery solutions for businesses requiring regular courier services in Reading, Bracknell, Slough, and surrounding areas. Contact us at 01344 508095 to discuss contract rates.'
  }
];

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>FAQ - Same Day Delivery Questions | AIS Express Berkshire</title>
        <meta name="description" content="Frequently asked questions about same-day delivery in Reading, Bracknell, Slough. Pricing, coverage, tracking, and more. Call 01344 508095" />
        <link rel="canonical" href="https://aisexpress.co.uk/faq" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-slate-600">
              Everything you need to know about our same-day delivery service in Berkshire & Hampshire
            </p>
          </div>

          {/* FAQ List */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card
                key={index}
                className="shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => toggleFAQ(index)}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-semibold text-slate-900 pr-8 flex-1">
                      {faq.question}
                    </h3>
                    <div className="flex-shrink-0">
                      {openIndex === index ? (
                        <ChevronUp className="h-6 w-6 text-emerald-600" />
                      ) : (
                        <ChevronDown className="h-6 w-6 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {openIndex === index && (
                    <div className="mt-4 text-slate-600 leading-relaxed border-t border-slate-200 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact CTA */}
          <Card className="mt-12 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-xl">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
              <p className="text-emerald-100 mb-6">
                Our team is available 24/7 to answer your questions and provide instant quotes
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:01344508095"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-emerald-700 rounded-md font-semibold hover:bg-emerald-50 transition-colors"
                >
                  📞 Call 01344 508095
                </a>
                <a
                  href="/book"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-white rounded-md font-semibold hover:bg-white/10 transition-colors"
                >
                  Get Instant Quote
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
